// SampleDemoClassData.jsx — Super Admin "Class Data Preview" for sample demos.
// v6.33.9 — Shows 15-student roster, growth-over-time per student, class
// proficiency distribution, modality radar, and draft ILPs. Deterministic
// per band; data served by GET /api/sample-demos/class-data/{band_token}.

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";
import { ArrowLeft, GraduationCap, TrendingUp, Users, Target, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mosaic_session_token";

// Palette — distinct, non-purple, non-generic.
const DOMAIN_COLORS = {
  listening: "#0ea5e9",   // sky
  reading:   "#f59e0b",   // amber
  speaking:  "#10b981",   // emerald
  writing:   "#ef4444",   // red
};
const LEVEL_COLORS = {
  Entering:   "#fecaca",
  Emerging:   "#fdba74",
  Developing: "#fde68a",
  Expanding:  "#bbf7d0",
  Bridging:   "#a7f3d0",
  Reaching:   "#86efac",
};

const SampleDemoClassData = () => {
  const { band } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const tok = localStorage.getItem(TOKEN_KEY);
        const res = await axios.get(
          `${API}/sample-demos/class-data/${encodeURIComponent(band)}`,
          tok ? { headers: { Authorization: `Bearer ${tok}` } } : {},
        );
        setData(res.data);
      } catch (err) {
        setError(err?.response?.data?.detail || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [band]);

  if (loading) {
    return <div className="p-8 text-slate-500" data-testid="sample-class-loading">Loading class data…</div>;
  }
  if (error) {
    return <div className="p-8 text-rose-600" data-testid="sample-class-error">Error: {error}</div>;
  }
  if (!data) return null;

  const student = data.students[selectedStudent];

  // Build growth-over-time rows for the selected student.
  const growthRows = student.progress_monitoring.map((p) => ({
    quarter: p.quarter,
    Listening: p.listening,
    Reading: p.reading,
    Speaking: p.speaking,
    Writing: p.writing,
  }));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sample-demo-class-data">
      {/* ─── Header ──────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/superadmin")}
            data-testid="back-to-superadmin"
          >
            <ArrowLeft size={16} className="mr-1" /> Back to Super Admin
          </Button>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              Sample Demo · Class Data Preview
            </div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <GraduationCap size={22} className="text-sky-600" />
              {data.band_label} — {data.teacher_name}
            </h1>
            <div className="text-sm text-slate-600">
              {data.school_name} · {data.class_size} students · deterministic sample data
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <Tabs defaultValue="roster" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="roster" data-testid="tab-roster">
              <Users size={14} className="mr-1" /> Roster
            </TabsTrigger>
            <TabsTrigger value="growth" data-testid="tab-growth">
              <TrendingUp size={14} className="mr-1" /> Growth Over Time
            </TabsTrigger>
            <TabsTrigger value="distribution" data-testid="tab-distribution">
              <Target size={14} className="mr-1" /> Class Distribution
            </TabsTrigger>
            <TabsTrigger value="radar" data-testid="tab-radar">
              <Target size={14} className="mr-1" /> Modality Radar
            </TabsTrigger>
            <TabsTrigger value="ilp" data-testid="tab-ilp">
              <FileText size={14} className="mr-1" /> ILP Drafts
            </TabsTrigger>
          </TabsList>

          {/* ─── Roster ─────────────────────────── */}
          <TabsContent value="roster">
            <Card className="p-0 overflow-hidden">
              <Table data-testid="roster-table">
                <TableHeader className="bg-slate-100">
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Baseline Level</TableHead>
                    <TableHead>Current Level</TableHead>
                    <TableHead>Growth</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Listening</TableHead>
                    <TableHead>Reading</TableHead>
                    <TableHead>Speaking</TableHead>
                    <TableHead>Writing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.students.map((s, i) => (
                    <TableRow
                      key={s.name}
                      className="cursor-pointer hover:bg-sky-50"
                      onClick={() => setSelectedStudent(i)}
                      data-testid={`roster-row-${i}`}
                    >
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell>
                        <span
                          className="px-2 py-0.5 text-xs rounded"
                          style={{ background: LEVEL_COLORS[s.baseline.level_name] }}
                        >
                          {s.baseline.level_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className="px-2 py-0.5 text-xs rounded"
                          style={{ background: LEVEL_COLORS[s.current.level_name] }}
                        >
                          {s.current.level_name}
                        </span>
                      </TableCell>
                      <TableCell className={s.growth > 0 ? "text-emerald-600 font-medium" : "text-slate-500"}>
                        {s.growth > 0 ? `+${s.growth}` : s.growth}
                      </TableCell>
                      <TableCell>{s.current.tier}</TableCell>
                      <TableCell>{s.current.by_domain.listening}</TableCell>
                      <TableCell>{s.current.by_domain.reading}</TableCell>
                      <TableCell>{s.current.by_domain.speaking}</TableCell>
                      <TableCell>{s.current.by_domain.writing}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <div className="text-xs text-slate-500 mt-2">
              Click any row to view that student's Growth and ILP in the next tabs.
            </div>
          </TabsContent>

          {/* ─── Growth over time ───────────────── */}
          <TabsContent value="growth">
            <Card className="p-4">
              <div className="mb-3">
                <div className="text-sm text-slate-500">Student</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <select
                    className="border border-slate-300 rounded px-2 py-1 text-sm"
                    value={selectedStudent}
                    onChange={(e) => setSelectedStudent(parseInt(e.target.value, 10))}
                    data-testid="growth-student-select"
                  >
                    {data.students.map((s, i) => (
                      <option key={s.name} value={i}>{s.name} ({s.current.level_name})</option>
                    ))}
                  </select>
                  <span className="text-sm text-slate-600">
                    Baseline <b>{student.baseline.level_name}</b> → Current <b>{student.current.level_name}</b>
                    &nbsp;·&nbsp; Growth <b className={student.growth > 0 ? "text-emerald-600" : ""}>
                      {student.growth > 0 ? `+${student.growth}` : student.growth}
                    </b>
                  </span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={growthRows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="quarter" />
                  <YAxis domain={[1, 6]} ticks={[1, 2, 3, 4, 5, 6]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Listening" stroke={DOMAIN_COLORS.listening} strokeWidth={2} />
                  <Line type="monotone" dataKey="Reading"   stroke={DOMAIN_COLORS.reading}   strokeWidth={2} />
                  <Line type="monotone" dataKey="Speaking"  stroke={DOMAIN_COLORS.speaking}  strokeWidth={2} />
                  <Line type="monotone" dataKey="Writing"   stroke={DOMAIN_COLORS.writing}   strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
              <div className="text-xs text-slate-500 mt-2">
                Y-axis: ELD proficiency 1 (Entering) → 6 (Reaching). 4 quarterly data points.
              </div>
            </Card>
          </TabsContent>

          {/* ─── Class distribution ─────────────── */}
          <TabsContent value="distribution">
            <Card className="p-4">
              <h3 className="font-bold mb-2">Class Proficiency Distribution by Domain</h3>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={data.distribution_rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="domain" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  {data.proficiency_levels.map((lv) => (
                    <Bar key={lv} dataKey={lv} stackId="a" fill={LEVEL_COLORS[lv]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <div className="text-xs text-slate-500 mt-2">
                Stacked bar: how many students sit at each proficiency level in each of the 4 domains.
              </div>
            </Card>
          </TabsContent>

          {/* ─── Modality radar ─────────────────── */}
          <TabsContent value="radar">
            <Card className="p-4">
              <h3 className="font-bold mb-2">Class Modality Radar (Average Level)</h3>
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart data={data.radar}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="domain" />
                  <PolarRadiusAxis angle={90} domain={[1, 6]} />
                  <Radar
                    name="Class average"
                    dataKey="class_average"
                    stroke="#0ea5e9"
                    fill="#0ea5e9"
                    fillOpacity={0.35}
                  />
                  <Tooltip />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
              <div className="text-xs text-slate-500 mt-2">
                Each spoke is a domain. Values are class-wide averages on the 1–6 ELD scale.
              </div>
            </Card>
          </TabsContent>

          {/* ─── ILP drafts ─────────────────────── */}
          <TabsContent value="ilp">
            <Card className="p-4">
              <div className="mb-3 flex items-center gap-3 flex-wrap">
                <select
                  className="border border-slate-300 rounded px-2 py-1 text-sm"
                  value={selectedStudent}
                  onChange={(e) => setSelectedStudent(parseInt(e.target.value, 10))}
                  data-testid="ilp-student-select"
                >
                  {data.students.map((s, i) => (
                    <option key={s.name} value={i}>{s.name} ({s.current.level_name})</option>
                  ))}
                </select>
                <span className="text-sm text-slate-600">
                  Draft Individual Learning Plan for <b>{student.name}</b>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {student.ilp.focus_domains.map((f) => (
                  <div
                    key={f.domain}
                    className="border border-slate-200 p-4 bg-white"
                    data-testid={`ilp-focus-${f.domain}`}
                  >
                    <div className="text-xs uppercase tracking-wider text-slate-500">
                      Focus Area · {f.domain}
                    </div>
                    <div className="font-bold text-slate-900 mb-2">
                      Currently {f.current_level_name} (Level {f.current_level})
                    </div>
                    <div className="text-sm text-slate-700 font-medium mb-1">
                      Recommended lessons:
                    </div>
                    <ul className="list-disc pl-5 text-sm text-slate-700">
                      {f.recommended_lessons.map((l, i) => <li key={i}>{l}</li>)}
                    </ul>
                  </div>
                ))}
                <div className="border border-emerald-200 p-4 bg-emerald-50">
                  <div className="text-xs uppercase tracking-wider text-emerald-700">
                    Strength
                  </div>
                  <div className="font-bold text-emerald-900">
                    {student.ilp.strength.domain.charAt(0).toUpperCase() + student.ilp.strength.domain.slice(1)}
                    &nbsp;· {student.ilp.strength.level_name} (Level {student.ilp.strength.level})
                  </div>
                  <div className="text-sm text-emerald-800 mt-1">
                    Use this domain to scaffold progress in the focus areas.
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SampleDemoClassData;
